import React, { useState, useEffect, useRef, useCallback, use } from "react";
import Login from "./Login.tsx";
import {Box,Typography} from "@mui/material";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Register from "./Register.tsx";
import CollectDob from "./CollectDob.tsx";
import TwoFactorSetup from "./TwoFactorSetup.tsx";
import ConsumerTwoFactorVerify from "./ConsumerTwoFactorVerify.tsx";
import SetPassword from "./SetPassword.tsx";
import Messages from "./Messages.tsx";
import CreditHistory from "./CreditHistory.tsx";
import Settings from "./Settings.tsx";
import "./Widget.css";
import { WidgetConfigResponse, ConsumerDetails } from "./types";
import { IconButton, Tab, Tabs, Badge, useMediaQuery, useTheme, Menu, MenuItem, Snackbar, Alert } from "@mui/material";
import { AddCircleRounded, Menu as MenuIcon } from "@mui/icons-material";
import CreditPurchase from "./CreditPurchase.tsx";
import PersonIcon from "@mui/icons-material/Person";
import ConsumerForgotPassword from "./ForgotPassword.tsx";
import { HubConnectionBuilder } from "@microsoft/signalr";
import PaymentResultPopup from "./PaymentResultPopup.tsx";


import coinIcon from "../../assets/coins.svg";
import { messagesApi,isShowConsumerToMessage } from "../../API/messagesApi.js";
import clientsApi from "../../API/clientsApi.js";
import { consumersApi } from "../../API/consumersApi.js";
import api from "../../API/axios.js";


// Snackbar notification types
type SnackbarSeverity = "success" | "error" | "warning" | "info";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
}

const Widget = () => {
  // Snackbar notification state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  // Show snackbar notification
  const showSnackbar = useCallback((message: string, severity: SnackbarSeverity = "info") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Hide snackbar notification
  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isVeryNarrow = useMediaQuery('(max-width:400px)'); // Very narrow screens

  // Hamburger menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuSelect = (newState: string) => {
    refreshAuthState();
    if (newState === "messages") {
      setUnreadMessageCount(0);
    }
    setState(newState);
    handleMenuClose();
  };

  const [consumerDetails, setConsumerDetails] =
    useState<ConsumerDetails | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false);
  const [state, setState] = useState<string>("login"); //login or loading
  const [pendingPassword, setPendingPassword] = useState(false);
  const [config, setConfig] = useState<WidgetConfigResponse | null>(null);
  const [auth, setAuth] = useState<any>({
    isAuthenticated: false,
  });
  const [showCredit, setShowCredit] = useState<number>(0);
  const[numberofminutes,setnumberofminutes] = useState(5);
  const[days,setnumberofdays] = useState(7);

  // Payment result state
  const [showPaymentResult, setShowPaymentResult] = useState<boolean>(false);
  const [paymentResult, setPaymentResult] = useState<{
    type: "success" | "failure" | "aborted";
    orderid?: string;
    status?: string;
    credits?: number;
    amount?: number;
    currency?: string;
  } | null>(null);

 const fetchAnalyticsConfig = async () => {
  try {
    const response = await api.get("/config/analytics");

    const minutes = response.data?.numberofminutes ?? 5;
    console.log("Minutes:", minutes);

    setnumberofminutes(minutes);
  } catch (err) {
    console.error("Failed to fetch analytics config, using default:", err);
    setnumberofminutes(5);
  }
};



  // Animate credit increase from old to new value over 3 seconds
  function animateCreditIncrease(
    from: number,
    to: number,
    setFn: (n: number) => void,
    duration = 3000
  ) {
    const start = from;
    const end = to;
    const range = end - start;
    if (range === 0) return;
    const stepTime = Math.abs(Math.floor(duration / Math.abs(range)));
    let current = start;
    const increment = end > start ? 1 : -1;
    const timer = setInterval(() => {
      current += increment;
      setFn(current);
      if (
        (increment > 0 && current >= end) ||
        (increment < 0 && current <= end)
      ) {
        clearInterval(timer);
        setFn(end);
      }
    }, stepTime);
  }
  const [unreadMessageCount, setUnreadMessageCount] = useState<number>(0);

  // Messages state for tracking consumer messages
  const [messages, setMessages] = useState<any[]>([]);
  const [ackMessage, setAckMessage] = useState<string | null>(null);
  // API-based ack banner state (for instant display when client has unreplied messages)
  const [showAckBannerFromApi, setShowAckBannerFromApi] = useState(false);
  // Timer ref for 5-minute auto-show banner
  const ackBannerTimerRef = useRef<NodeJS.Timeout | null>(null);

  // SignalR connection for global notifications (across all tabs)
  const connectionRef = useRef<any>(null);
  const [isSignalRConnected, setIsSignalRConnected] = useState(false);

  // Notification system (same as admin portal)
  const lastNotificationRef = useRef<string | null>(null);
  const notificationCooldownRef = useRef<number | null>(null);
  const lastSoundRef = useRef(0);
  const lastCountIncrementRef = useRef(0);
  const NOTIFICATION_COOLDOWN = 2000; // 2 seconds between similar notifications
  const SOUND_COOLDOWN = 1000; // 1 second between sounds
  const COUNT_INCREMENT_COOLDOWN = 2000; // 2 seconds between count increments
  const [pending2FA, setPending2FAState] = useState(() => {
    // Restore pending2FA from localStorage if present
    const stored = localStorage.getItem("pending2FA");
    return stored ? JSON.parse(stored) : null;
  }); // { consumerId, email }

  // Pending OAuth data for DOB collection after social login
  const [pendingOAuthData, setPendingOAuthDataState] = useState<{
    provider: "google" | "apple" | "facebook";
    email: string;
    name?: string;
    token?: string;
    code?: string;
    clientId: number;
  } | null>(() => {
    // Restore pendingOAuthData from localStorage if present
    const stored = localStorage.getItem("pendingOAuthData");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Helper to persist pendingOAuthData in localStorage
  const setPendingOAuthData = (value: typeof pendingOAuthData) => {
    setPendingOAuthDataState(value);
    if (value) {
      localStorage.setItem("pendingOAuthData", JSON.stringify(value));
    } else {
      localStorage.removeItem("pendingOAuthData");
    }
  };

const [now, setNow] = useState(Date.now());

  // Helper to persist pending2FA in localStorage
  const setPending2FA = (value: any) => {
    setPending2FAState(value);
    if (value) {
      localStorage.setItem("pending2FA", JSON.stringify(value));
    } else {
      localStorage.removeItem("pending2FA");
    }
  };
  const [error, setError] = useState("");

  // Client-specific token storage helpers
  const getConsumerToken = () => {
    if (!config?.clientId) return null;
    return localStorage.getItem(`consumerToken_${config.clientId}`);
  };

  const setConsumerToken = (token: string) => {
    if (!config?.clientId) return;
    localStorage.setItem(`consumerToken_${config.clientId}`, token);
  };

  const removeConsumerToken = () => {
    if (!config?.clientId) return;
    localStorage.removeItem(`consumerToken_${config.clientId}`);
  };

  // Helper function to make API requests with client context
  const makeConsumerRequest = async (url: string, options: RequestInit = {}) => {
    const token = getConsumerToken();
    const headers = {
      ...options.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(config?.clientId && { "X-Client-Id": config.clientId.toString() }),
    };

    return fetch(url, { ...options, headers });
  };

  // Helper function to show notifications with deduplication (same as admin portal)
  const showNotification = (title: string, body: string) => {
    const notificationKey = `${title}:${body}`;
    const now = Date.now();

    // Check if this is a duplicate notification within cooldown period
    if (
      lastNotificationRef.current === notificationKey &&
      notificationCooldownRef.current &&
      now - notificationCooldownRef.current < NOTIFICATION_COOLDOWN
    ) {
      console.log(
        "[Consumer Widget] 🚫 Duplicate notification blocked (cooldown active):",
        title,
        body
      );
      return;
    }

    // Update deduplication tracking
    lastNotificationRef.current = notificationKey;
    notificationCooldownRef.current = now;

    console.log(
      "[Consumer Widget] Attempting to show notification:",
      title,
      body
    );

    if (!window.Notification) {
      console.log(
        "[Consumer Widget] ❌ Browser does not support notifications"
      );
      return;
    }

    if (Notification.permission === "granted") {
      try {
        console.log("[Consumer Widget] 🔔 Creating browser notification...");

        const notification = new Notification(title, {
          body: body,
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          tag: "webchat-consumer-message",
          requireInteraction: false,
          silent: false,
        });

        notification.onclick = () => {
          console.log(
            "[Consumer Widget] 👆 User clicked notification - focusing window"
          );
          window.focus();
          notification.close();
        };

        notification.onshow = () => {
          console.log(
            "[Consumer Widget] ✅ Browser notification is now visible:",
            title
          );
        };

        notification.onclose = () => {
          console.log("[Consumer Widget] 📱 Notification closed:", title);
        };

        // Auto-close after 8 seconds
        setTimeout(() => {
          notification.close();
        }, 8000);

        console.log(
          "[Consumer Widget] ✅ Browser notification created successfully:",
          title,
          body
        );
      } catch (error) {
        console.error(
          "[Consumer Widget] ❌ Error creating notification:",
          error
        );
      }
    } else if (Notification.permission === "default") {
      console.log("[Consumer Widget] 📋 Requesting notification permission...");
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          showNotification(title, body); // Retry showing notification
        }
      });
    }
  };

  
  // Helper function to play notification sound (same as admin portal)
  const playNotificationSound = () => {
    const now = Date.now();

    // Throttle sound to prevent spam
    if (now - lastSoundRef.current < SOUND_COOLDOWN) {
      console.log("[Consumer Widget] 🚫 Sound blocked (cooldown active)");
      return;
    }

    lastSoundRef.current = now;

    try {
      // Use Web Audio API directly instead of file (more reliable)
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Create a pleasant notification tone
      oscillator.frequency.value = 800;
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      console.log(
        "[Consumer Widget] ✅ Notification sound played (Web Audio API)"
      );
    } catch (error) {
      console.error(
        "[Consumer Widget] Error playing notification sound:",
        error
      );
    }
  };

  // Function to fetch messages for checking thank you message eligibility
  const fetchMessagesForBanner = async () => {
    try {
      const token = getConsumerToken();
      if (!token) {
        console.log("[Consumer Widget] No token to fetch messages");
        return;
      }

      const response = await fetch("/api/consumer/messages?limit=100", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Client-Id": config?.clientId?.toString() || "",
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("[Consumer Widget] 📨 Messages fetched for banner check:", data.messages);
        setMessages(data.messages || []);
      } else {
        console.error("[Consumer Widget] Failed to fetch messages:", response.status);
      }
    } catch (error) {
      console.error("[Consumer Widget] Error fetching messages:", error);
    }
  };

  const initializeSignalR = async () => {
    const token = getConsumerToken();
    if (!token || !auth.isAuthenticated) {
      console.log(
        "[Consumer Widget] No token or not authenticated, skipping SignalR initialization"
      );
      return;
    }

    if (connectionRef.current) {
      console.log("[Consumer Widget] SignalR connection already exists");
      return;
    }

    console.log(
      "[Consumer Widget] Initializing SignalR connection for global notifications..."
    );

    // Request browser notification permission
    if (
      window.Notification &&
      Notification.permission !== "granted" &&
      Notification.permission !== "denied"
    ) {
      console.log("[Consumer Widget] 📋 Requesting notification permission...");
      Notification.requestPermission().then((permission) => {
        console.log("[Consumer Widget] Permission result:", permission);
        if (permission === "granted") {
          showNotification(
            "🎉 WebChat Notifications Enabled!",
            "You will now receive alerts for new messages from support"
          );
        }
      });
    }

      const apiUrl = process.env.REACT_APP_URL || "https://webchat.supportedbusiness.com"; // Use env variable with fallback
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiUrl}/hubs/messaging`, {
        accessTokenFactory: () => token || "",
      })
      .withAutomaticReconnect()
      .build();

      // Handle OperatorResponse events (primary event for consumer notifications)
      // This is the ONLY event that should increment count to avoid duplicates
      connection.on("OperatorResponse", (response: any) => {
        
        // Reset API-based ack banner when operator replies
        setShowAckBannerFromApi(false);
        
        // Clear any pending 5-minute banner timer since operator replied
        if (ackBannerTimerRef.current) {
          clearTimeout(ackBannerTimerRef.current);
          ackBannerTimerRef.current = null;
          console.log("⏱️ Cleared 5-minute banner timer - operator replied");
        }
        
        // OPTIMIZATION: Update messages state in real-time to hide banner immediately
        setMessages(prevMessages => {
          const newMessage = {
            id: response.MessageId || response.messageId || `signalr-${Date.now()}`,
            body: response.MessageBody || response.messageBody || response.MessagePreview || response.messagePreview,
            direction: 'outbound', // Operator messages are outbound
            timestamp: response.CreatedAt || response.createdAt || new Date().toISOString(),
            status: 'delivered'
          };
          
        
          return [...prevMessages, newMessage];
        });
        
        // OPTIMIZATION: Pass message data directly to Messages component (avoid REST reload)
        // Use full message body (MessageBody) instead of truncated preview
        console.log('[Consumer Widget] 📨 Sending message data to Messages component');
        window.dispatchEvent(new CustomEvent('operator-message-received', {
          detail: {
            conversationId: response.ConversationId || response.conversationId,
            messageBody: response.MessageBody || response.messageBody || response.MessagePreview || response.messagePreview,
            messagePreview: response.MessagePreview || response.messagePreview,
            operatorName: response.OperatorName || response.operatorName,
            createdAt: response.CreatedAt || response.createdAt || new Date().toISOString()
          }
        }));
        
        const now = Date.now();
        
        // Throttle count increment to prevent duplicate increments
        if (now - lastCountIncrementRef.current < COUNT_INCREMENT_COOLDOWN) {
          console.log('[Consumer Widget] 🚫 Count increment blocked (cooldown active)');
          return; // Exit early if cooldown is active
        }
        
        lastCountIncrementRef.current = now;
        
        // Increment unread count GLOBALLY (only once per message)
        setUnreadMessageCount(prev => {
          const newCount = prev + 1;
          console.log('[Consumer Widget] 📊 Unread count updated:', prev, '→', newCount);
          return newCount;
        });
        
        // Update browser tab title
        const originalTitle = document.title;
        document.title = `🔴 New Response - WebChat`;
        
        // ALWAYS show notification and play sound (regardless of current tab)
        showNotification(
          '💬 New Response from Support',
          'You have received a new response from support'
        );
        
        // ALWAYS play sound (regardless of current tab)
        playNotificationSound();
        
        // Reset title after 10 seconds or when user focuses the window
        const resetTitle = () => {
          document.title = originalTitle || 'WebChat';
        };
        
        const titleResetTimer = setTimeout(resetTitle, 10000);
        
        // Reset immediately if user focuses the window
        const handleFocus = () => {
          clearTimeout(titleResetTimer);
          resetTitle();
          window.removeEventListener('focus', handleFocus);
        };
        
        window.addEventListener('focus', handleFocus);
      });

    // Handle MessageReceived events (for logging only - no duplicate counting)
    connection.on("MessageReceived", (message: any) => {
      console.log(
        "[Consumer Widget] 📋 MessageReceived event (GLOBAL - info only):",
        message
      );
      // NOTE: We don't increment unread count here to avoid duplicates
      // The OperatorResponse event already handles count increment and notifications
    });

    connection.onclose(() => setIsSignalRConnected(false));
    connection.onreconnected(() => setIsSignalRConnected(true));
    connection.onreconnecting(() => setIsSignalRConnected(false));

    try {
      await connection.start();
      setIsSignalRConnected(true);
      console.log(
        "[Consumer Widget] ✅ SignalR connected globally for notifications!"
      );
      connectionRef.current = connection;
    } catch (error) {
      console.error("[Consumer Widget] ❌ SignalR connection failed:", error);
      setIsSignalRConnected(false);
    }
  };

  // Payment result handlers
  const handlePaymentSuccess = async (orderId: string, clientKey: string) => {
    console.log("🎉 Payment SUCCESS handler called for order:", orderId);
    
    // Show immediate success popup (like failure handler)
    setPaymentResult({
      type: "success",
      orderid: orderId,
      status: "Payment successful! Credits are being added to your account."
    });
    setShowPaymentResult(true);
    
    // Then do the API call in background to get actual credit amount
    console.log("🔍 Config clientId:", config?.clientId);
    
    try {
      // Fetch order details to get credit amount using correct API endpoint
      const response = await fetch(`/api/InveroakPayment/order-status`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          orderId: orderId, 
          clientId: config?.clientId || 0 
        })
      });
      
      console.log("🌐 API Response status:", response.status, response.ok);
      
      if (response.ok) {
        const orderData = await response.json();
        console.log("Order data received:", orderData);
        
        if (orderData.paymentstatus === 'Success' && orderData.credits) {
          // Update popup with actual credit amount
          setPaymentResult({
            type: "success",
            orderid: orderId,
            status: `Payment successful! ${orderData.credits} credits added to your account.`,
            credits: orderData.credits
          });
          
          // First fetch the actual current balance from server
          // Then animate from current showCredit to new balance
          if (auth.isAuthenticated) {
            const token = getConsumerToken();
            const consumerId = auth?.consumerId || auth?.id;
            
            if (consumerId && token) {
              try {
                const creditResponse = await fetch(
                  `/api/consumer/${consumerId}/credits/balance`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                      "X-Client-Id": config?.clientId?.toString() || "",
                    },
                  }
                );
                
                if (creditResponse.ok) {
                  const creditData = await creditResponse.json();
                  const newBalance = creditData.data ?? creditData;
                  console.log("💰 New balance after payment:", newBalance, "Current showCredit:", showCredit);
                  
                  // Animate from current displayed value to new balance
                  const currentDisplayed = showCredit || 0;
                  if (newBalance > currentDisplayed) {
                    animateCreditIncrease(currentDisplayed, newBalance, setShowCredit);
                  } else {
                    // Just set directly if no increase (edge case)
                    setShowCredit(newBalance);
                  }
                }
              } catch (creditError) {
                console.error("Error fetching credit balance:", creditError);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
     
    }
  };

  const handlePaymentFailure = (orderId: string) => {
    console.log("Payment failed for order:", orderId);
    setPaymentResult({
      type: "failure",
      orderid: orderId,
      status: "Payment was not processed. Please try again."
    });
    setShowPaymentResult(true);
  };

  const handlePaymentAborted = (orderId: string) => {
    console.log("Payment aborted for order:", orderId);
    setPaymentResult({
      type: "aborted",
      orderid: orderId,
      status: "Payment was cancelled. You can try again anytime."
    });
    setShowPaymentResult(true);
  };

  // Initialize SignalR connection for global notifications (works across all tabs)
  useEffect(() => {
    if (auth.isAuthenticated) {
      initializeSignalR();
    }
    return () => {
      if (connectionRef.current) {
        console.log("[Consumer Widget] Cleaning up global SignalR connection");
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [auth.isAuthenticated]);

  useEffect(() => {
    initializeWidget();

    // Listen for mark as read events from Messages component
    const handleMessagesMarkedAsRead = () => {
      console.log('[Consumer Widget] 📖 Messages marked as read - resetting notification count to 0');
      setUnreadMessageCount(0);
    };

    // Listen for messages from parent window
    const handleMessage = (event: any) => {
      if (event.data.type === "sbw-open") {
        // Widget opened, refresh if needed
        if (auth.isAuthenticated) {
          refreshAuthState();
        }
      }
    };

    // Add event listeners
    window.addEventListener('messages-marked-as-read', handleMessagesMarkedAsRead);
    window.addEventListener("message", handleMessage);
    
    return () => {
      window.removeEventListener('messages-marked-as-read', handleMessagesMarkedAsRead);
      window.removeEventListener("message", handleMessage);
    };
  }, [auth.isAuthenticated]);

  // Payment result detection
  useEffect(() => {
    console.log("🔍 Payment detection effect running...");
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const orderId = urlParams.get('orderid');
    const clientKey = urlParams.get('clientKey');

    console.log("📋 URL Params:", { payment, orderId, clientKey, configExists: !!config });

    if (payment && orderId && clientKey && config) {
      console.log('✅ Payment result detected:', { payment, orderId, clientKey });
      
      // Store clientKey in sessionStorage to ensure widget can reinitialize
      sessionStorage.setItem('widgetClientKey', clientKey);
      
      switch (payment) {
        case 'success':
          console.log("🎉 Calling handlePaymentSuccess...");
          handlePaymentSuccess(orderId, clientKey);
          break;
        case 'failure':
          console.log("❌ Calling handlePaymentFailure...");
          handlePaymentFailure(orderId);
          break;
        case 'aborted':
          console.log("🚫 Calling handlePaymentAborted...");
          handlePaymentAborted(orderId);
          break;
        default:
          console.warn('❓ Unknown payment status:', payment);
      }

      // Clean up URL parameters but preserve clientKey for widget initialization
      const newUrl = `${window.location.pathname}?clientKey=${clientKey}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [config]); // Wait for config to be loaded before processing payment results

  const initializeWidget = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      let clientKey = urlParams.get("clientKey");
      
      // Fall back to sessionStorage if clientKey not in URL (e.g., after payment redirect cleanup)
      if (!clientKey) {
        clientKey = sessionStorage.getItem('widgetClientKey');
      }

      if (!clientKey) {
        throw new Error("No client key provided");
      }

      const response = await fetch(
        `/api/public/widget-config?clientKey=${clientKey}`
      );

      if (!response.ok) {
        throw new Error("Failed to load widget configuration");
      }

      const widgetConfig = await response.json();
      setConfig(widgetConfig);

      // Check if user is already authenticated for THIS client
      const token = localStorage.getItem(`consumerToken_${widgetConfig.clientId}`);
      if (token) {
        try {
          const authResponse = await fetch("/api/consumer/profile", {
            headers: { 
              Authorization: `Bearer ${token}`,
              "X-Client-Id": widgetConfig.clientId.toString()
            },
          });
          if (authResponse.ok) {
            const profile = await authResponse.json();
            // Fetch dynamic credit balance from ledger
            let dynamicCredits = profile.credits;
            try {
              const balanceResponse = await fetch(
                `/api/consumer/${profile.id}/credits/balance`,
                {
                  headers: { 
                    Authorization: `Bearer ${token}`,
                    "X-Client-Id": widgetConfig.clientId.toString()
                  },
                }
              );
              const balanceData = await balanceResponse.json();
              if (balanceResponse.ok && balanceData?.data !== undefined) {
                dynamicCredits = balanceData.data;
              }
            } catch {}
            setAuth({
              isAuthenticated: true,
              consumerId: profile.id,
              email: profile.email,
              clientId: widgetConfig.clientId,
              credits: dynamicCredits,
              name: profile.firstName || "",
              surname: profile.surname || "",
              telephone: profile.telephone || "",
              twoFactorMethod: profile.twoFactorMethod || "none",
              country: profile.country || "",
              state: profile.state || "",
              city: profile.city || "",
              postcode: profile.postcode || "",
              address: profile.address || "",
              address2: profile.address2 || "",
              ssoProvider: profile.ssoProvider || null,
            });
            // Set the credit display immediately
            setShowCredit(dynamicCredits);
            setState("credits");
            return;
          } else {
            localStorage.removeItem(`consumerToken_${widgetConfig.clientId}`);
          }
        } catch {
          localStorage.removeItem(`consumerToken_${widgetConfig.clientId}`);
        }
      }
      // If no token, check for pending2FA in localStorage
      const pending2FAStored = localStorage.getItem("pending2FA");
      if (pending2FAStored) {
        try {
          const parsed = JSON.parse(pending2FAStored);
          if (parsed && parsed.consumerId && parsed.email) {
            setPending2FA(parsed);
            setState("twoFactorVerify");
            return;
          }
        } catch {}
        // If parsing fails, clear it
        localStorage.removeItem("pending2FA");
      }
      
      // Check for pending registration in localStorage (client-side multi-step flow)
      const pendingRegStored = localStorage.getItem(`pendingRegistration_${widgetConfig.clientId}`);
      if (pendingRegStored) {
        try {
          const pendingReg = JSON.parse(pendingRegStored);
          if (pendingReg && pendingReg.email && pendingReg.clientId) {
            // Set auth data for display purposes
            setAuth({
              isAuthenticated: false,
              email: pendingReg.email,
              clientId: pendingReg.clientId,
            });
            
            // Determine which step to resume based on stored data
            if (pendingReg.step === 'twoFactorSetup' || pendingReg.password) {
              // Password set, need 2FA setup
              setState("twoFactorSetup");
              return;
            } else if (pendingReg.step === 'setPassword' || pendingReg.firstName) {
              // Basic info collected, need password
              setState("setPassword");
              return;
            }
          }
        } catch (e) {
          console.error("Error checking pending registration:", e);
          // If parsing fails, clear it
          localStorage.removeItem(`pendingRegistration_${widgetConfig.clientId}`);
        }
      }
      
      setState("login");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to initialize widget"
      );
      setState("error");
    }
  };

  const refreshAuthState = async () => {
    const token = getConsumerToken();
    if (!token) return;

    try {
      const response = await fetch("/api/consumer/profile", {
        headers: { 
          Authorization: `Bearer ${token}`,
          "X-Client-Id": config?.clientId?.toString() || ""
        },
      });

      // Handle token expiry - logout and redirect to login
      if (response.status === 401) {
        console.log('🔒 Consumer token expired - logging out');
        removeConsumerToken();
        setAuth({ isAuthenticated: false });
        setState("login");
        return;
      }

      if (response.ok) {
        const profile = await response.json();
        // Fetch dynamic credit balance from ledger
        let dynamicCredits = profile.credits;
        try {
          const balanceResponse = await fetch(
            `/api/consumer/${profile.id}/credits/balance`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const balanceData = await balanceResponse.json();
          if (balanceResponse.ok && balanceData?.data !== undefined) {
            dynamicCredits = balanceData.data;
          }
        } catch {}
        setAuth({
          isAuthenticated: true,
          consumerId: profile.id, // Use consumerId for consistency
          email: profile.email,
          clientId: profile.clientId,
          name: profile.firstName || "",
          surname: profile.surname || "",
          telephone: profile.telephone || "",
          twoFactorMethod: profile.twoFactorMethod || "none",
          country: profile.country || "",
          state: profile.state || "",
          city: profile.city || "",
          postcode: profile.postcode || "",
          address: profile.address || "",
          address2: profile.address2 || "",
          credits: dynamicCredits,
          ssoProvider: profile.ssoProvider || null,
        });
        // Update credit display immediately
        setShowCredit(dynamicCredits);
        setConsumerDetails(profile);
      }
    } catch (error) {
      console.error("Failed to refresh auth state:", error);
    }
  };

  const handleAuthSuccess = (authData: any) => {
    console.log("Widget handleAuthSuccess called with:", authData);
    // Do NOT set isAuthenticated: true yet; wait until password and 2FA are complete
    if (state === "register") {
      // Registration just completed, require password setup
      setAuth({
        ...authData,
        isAuthenticated: false, // Not authenticated until password/2FA complete
      });
      setPendingPassword(true);
      setState("setPassword");
      return;
    }
    if (authData.requiresTwoFactor) {
      // If this is registration, go to setup; if login, go to verify
      if (authData.consumerId && !authData.token) {
        setPending2FA({
          consumerId: authData.consumerId,
          email: authData.email,
        });
        setState("twoFactorVerify");
      } else {
        setState("twoFactorSetup");
      }
    } else {
      // Only set isAuthenticated: true if password/2FA not required
      // Store token with client-specific key
      if (authData.token) {
        setConsumerToken(authData.token);
      }
      setAuth({
        ...authData,
        isAuthenticated: true,
      });
      setState("credits");
      // Fetch consumer details immediately so Purchase Credit modal works
      refreshAuthState();
    }
  };
  // 2FA verify handler
  const handle2FAVerified = (token: any, data: any) => {
    setConsumerToken(token);
    setAuth({
      isAuthenticated: true,
      consumerId: data.consumerId,
      email: data.email,
      clientId: config?.clientId,
      credits: data.credits,
    });
    setPending2FA(null); // This will also clear from localStorage
    setState("credits");
    // Fetch consumer details immediately so Purchase Credit modal works
    refreshAuthState();
  };

  useEffect(() => {
    // Fetch credit balance and messages when auth changes and user is authenticated
    if (auth.isAuthenticated && (auth.consumerId || auth.id)) {
      console.log("🔄 Fetching credit balance for authenticated user:", auth.consumerId || auth.id);
      fetchCreditBalance(auth);
      // Also fetch messages for thank you banner eligibility check
      fetchMessagesForBanner();
      // Fetch admin-saved acknowledgement message (if any)
      (async () => {
        try {
          const clientId = auth.clientId;
          if (!clientId) return;
          const res = await consumersApi.getConsumerAcknowledgement(clientId);
          // API returns payload in `data` (nullable)
          const ack = res?.data ?? null;
          if (ack && ack.message) {
           
            setAckMessage(ack.message);
          } else {
            setAckMessage(null);
          }
        } catch (err) {
          console.error("Failed to load acknowledgement message:", err);
          setAckMessage(null);
        }
      })();
    }
  }, [auth.isAuthenticated, auth.consumerId, auth.id]);

  // useEffect(() => {
  //   if (!auth?.isAuthenticated) return;
  //   const interval = setInterval(() => {
  //     fetchMessagesForBanner();
  //   }, 300000); // 30 seconds

  //   return () => clearInterval(interval);
  // }, [auth?.isAuthenticated]);
  useEffect(() => {
  if (!auth?.isAuthenticated || !numberofminutes) return;

  const intervalInMs = numberofminutes * 60 * 1000;

  const interval = setInterval(() => {
    fetchMessagesForBanner();
  }, intervalInMs);

  return () => clearInterval(interval);
}, [auth?.isAuthenticated, numberofminutes]);
useEffect(() => {
  if (auth?.isAuthenticated) {
    fetchAnalyticsConfig();
  }
}, [auth?.isAuthenticated]);


  const fetchAckMessage = async () => {
    if(auth && auth.clientId){
      const res = await consumersApi.getConsumerAcknowledgement(auth.clientId);
      console.log("Acknowledgement message fetch result:", res);
    }}
  useEffect(() => {
    fetchAckMessage();
    },[]);

  /**
   * Callback called by Messages component after a message is sent.
   * Immediately checks backend ack status to show banner if client has unreplied messages > 5 min.
   * 
   * The backend will return shouldShow: true ONLY if:
   * 1. AcknowledgementTriggeredAt was set (meaning client had old unreplied messages when this consumer sent their message)
   * 2. OR client has unreplied messages > 5 min from OTHER consumers
   */
  const handleMessageSent = useCallback(async (conversationId: number | null) => {
    console.log("🔔 Message sent callback, conversationId:", conversationId);
    
    if (!conversationId) {
      console.log("⚠️ No conversationId, skipping ack status check");
      return;
    }
    try {
      // Get token directly to avoid stale closure issues
      const token = localStorage.getItem(`consumerToken_${config?.clientId}`);
      console.log("🔔 Token for ack-status:", token ? "present" : "missing", "clientId:", config?.clientId);
      
      if (!token) {
        console.log("⚠️ No consumer token, skipping ack status check");
        return;
      }
      
      const response = await fetch(`/api/consumer/conversation/${conversationId}/ack-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Client-Id': config?.clientId?.toString() || '',
        },
      });
      
      if (response.ok) {
        const ackStatus = await response.json();
        console.log("🔔 Ack status from API:", ackStatus);
        if (ackStatus.shouldShow) {
          console.log("🔔 Setting showAckBannerFromApi to TRUE");
          setShowAckBannerFromApi(true);
          // Clear any existing timer since we're showing banner now
          if (ackBannerTimerRef.current) {
            clearTimeout(ackBannerTimerRef.current);
            ackBannerTimerRef.current = null;
          }
        } else {
          console.log("🔔 Ack status shouldShow is false - setting 5 min timer");
          // Clear any existing timer before setting new one
          if (ackBannerTimerRef.current) {
            clearTimeout(ackBannerTimerRef.current);
          }
          // Set a 5-minute timer to auto-show banner if operator doesn't reply
          ackBannerTimerRef.current = setTimeout(() => {
            console.log("⏱️ 5-minute timer fired - showing banner");
            setShowAckBannerFromApi(true);
            ackBannerTimerRef.current = null;
          }, 5 * 60 * 1000); // 5 minutes
          console.log("⏱️ Started 5-minute banner timer");
        }
      } else {
        console.error("🔔 Failed to fetch ack status:", response.status);
      }
    } catch (error) {
      console.error("Failed to fetch ack status after message send:", error);
    }
  }, [config?.clientId]);

  const fetchCreditBalance = async (auth: any) => {
    try {
      const token = getConsumerToken();
      const consumerId = auth?.consumerId || auth?.id;
      console.log("💰 Fetching credit balance for consumer:", consumerId);
      
      if (consumerId && token) {
        const creditResponse = await fetch(
          `/api/consumer/${consumerId}/credits/balance`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Client-Id": config?.clientId?.toString() || "",
            },
          }
        );
        console.log("💰 Credit response:", creditResponse.status, creditResponse.ok);
        if (creditResponse.ok) {
          const creditData = await creditResponse.json();
          console.log("💰 Credit data:", creditData);
          const balance = creditData.data ?? creditData;
          console.log("💰 Setting credit balance to:", balance);
          setShowCredit(balance);
        } else {
          console.error("💰 Credit fetch failed:", creditResponse.status);
        }
      } else {
        console.log("💰 No consumer ID or token available");
      }
    } catch (error) {
      console.error("💰 Credit error:", error);
    }
  };











  const handleLogout = () => {
    removeConsumerToken();
    setAuth({ isAuthenticated: false });
    setState("login");
  };

  // Periodic check every 30 seconds to refresh messages for thank you banner

  const sendMessageToParent = (type: any, data: any = {}) => {
    window.parent.postMessage({ type, ...data }, "*");
  };

  if (state === "loading") {
    return (
      <div className="widget-container">
        <div className="widget-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="widget-container">
        <div className="widget-error">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={initializeWidget}>Retry</button>
        </div>
      </div>
    );
  }

  const commonProps = {
    config,
    auth,
    onAuthSuccess: handleAuthSuccess,
    onStateChange: setState,
    sendMessageToParent,
    // Add token helper functions
    getConsumerToken,
    setConsumerToken,
    removeConsumerToken,
    makeConsumerRequest,
    // Snackbar notification
    showSnackbar,
  };

  function capitalizeFirstLetter(str?: string) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <div className="widget-container">
      {auth.isAuthenticated &&
        (state === "messages" ||
          state === "credits" ||
          state === "settings") && (
          <div className="widget-header">
            {/* Left section: User info + Navigation */}
            <div className="header-left">
              <div className="header-user">
                <PersonIcon className="header-user-icon" />
                <h2 className="header-user-name">
                  {capitalizeFirstLetter(auth?.name) || "WebChat"}
                </h2>
              </div>
              
              {/* Hamburger menu for very narrow screens */}
              {isVeryNarrow ? (
                <div className="header-tabs">
                  <IconButton
                    aria-label="menu"
                    onClick={handleMenuOpen}
                    className="header-menu-btn"
                  >
                    <MenuIcon sx={{ fontSize: 16 }} />
                    <span style={{ fontSize: 10, marginLeft: 2 }}>
                      {state === "credits" ? "Credits" : state === "messages" ? "Messages" : "Settings"}
                    </span>
                    {unreadMessageCount > 0 && state !== "messages" && (
                      <Badge 
                        badgeContent={unreadMessageCount} 
                        color="error" 
                        sx={{ 
                          ml: 1,
                          "& .MuiBadge-badge": { fontSize: "0.5rem", minWidth: 14, height: 14 }
                        }} 
                      />
                    )}
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem 
                      onClick={() => handleMenuSelect("credits")}
                      selected={state === "credits"}
                      sx={{ 
                        color: state === "credits" ? "#a12a22" : "inherit",
                        fontWeight: state === "credits" ? 600 : 400,
                      }}
                    >
                      Credit History
                    </MenuItem>
                    <MenuItem 
                      onClick={() => handleMenuSelect("messages")}
                      selected={state === "messages"}
                      sx={{ 
                        color: state === "messages" ? "#a12a22" : "inherit",
                        fontWeight: state === "messages" ? 600 : 400,
                      }}
                    >
                      Messages
                      {unreadMessageCount > 0 && (
                        <Badge 
                          badgeContent={unreadMessageCount} 
                          color="error" 
                          sx={{ ml: 2 }} 
                        />
                      )}
                    </MenuItem>
                    <MenuItem 
                      onClick={() => handleMenuSelect("settings")}
                      selected={state === "settings"}
                      sx={{ 
                        color: state === "settings" ? "#a12a22" : "inherit",
                        fontWeight: state === "settings" ? 600 : 400,
                      }}
                    >
                      Settings
                    </MenuItem>
                  </Menu>
                    
                </div>
                

                
              ) : (
                /* Regular Tabs for normal screens */
                <div className="header-tabs">
                  <Tabs
                    value={state === "credits" ? 0 : state === "messages" ? 1 : 2}
                    onChange={(_e, val) => {
                      refreshAuthState();
                      if (val === 0) setState("credits");
                      else if (val === 1) {
                        setState("messages");
                        setUnreadMessageCount(0);
                      } else setState("settings");
                    }}
                    aria-label="Portal Tabs"
                    textColor="inherit"
                    TabIndicatorProps={{ style: { backgroundColor: "#a12a22" } }}
                    sx={{
                      "& .MuiTab-root": { 
                        color: "#555", 
                        textTransform: "none",
                      },
                      "& .Mui-selected": {
                        color: "#a12a22",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <Tab label={isMobile ? "Credits" : "Credit History"} />
                    <Tab
                      label={
                        unreadMessageCount > 0 ? (
                          <Badge
                            badgeContent={unreadMessageCount}
                            color="error"
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                top: "-1px",
                                right: "-6px",
                              },
                            }}
                          >
                            <span>Messages</span>
                          </Badge>
                        ) : (
                          "Messages"
                        )
                      }
                    />
                   
                    <Tab label="Settings" />
                  </Tabs>
                </div>
              )}
            </div>
          
   
            {/* Right section: Credits display */}
            <div className="header-right">
              <img
                src={coinIcon}
                alt="coin"
                className="header-coin-icon"
              />
              <div className="header-credit-info">
                <span className="header-credit-label">
                  {isMobile ? "Credit" : "Available credit"}
                </span>
                <span className="header-credit-value">
                  {showCredit}
                </span>
              </div>
              <IconButton
                onClick={() => setShowPurchaseModal(true)}
                className="header-add-btn"
              >
                <AddCircleRounded />
              </IconButton>
            </div>
          </div>
        )}
  {(isShowConsumerToMessage(messages) || showAckBannerFromApi) && state === "messages" && (
  <Box sx={{ position: "sticky", top: 0, zIndex: 9999, display: "flex", gap: 1, p: 1, bgcolor: "#fff3e0" }}>
    <ErrorOutline sx={{ fontSize: 20 }} />
    <Typography variant="caption">
      {ackMessage
        ? ackMessage
        : "Thank you for your message. We're busy at the moment answering a large number of messages but we'll answer as quickly as possible."}
    </Typography>
  </Box>
)}

      <div className="widget-content">
        {state === "twoFactorVerify" && pending2FA && (
          <ConsumerTwoFactorVerify
            consumerId={pending2FA.consumerId}
            email={pending2FA.email}
            onSuccess={handle2FAVerified}
            onError={(msg) => setError(msg)}
            setState={setState}
          />
        )}
        {/* Auth/registration flow */}
        {state === "login" && <Login {...commonProps} />}
        {state === "forgotPassword" && (
          <ConsumerForgotPassword setState={setState} />
        )}
        {state === "register" && <Register {...commonProps} setPendingOAuthData={setPendingOAuthData} />}
        {state === "collectDob" && pendingOAuthData && (
          <CollectDob
            config={config}
            pendingOAuthData={pendingOAuthData}
            onAuthSuccess={handleAuthSuccess}
            onStateChange={setState}
            setConsumerToken={setConsumerToken}
            showSnackbar={showSnackbar}
            onCancel={() => {
              setPendingOAuthData(null);
              setState("register");
            }}
          />
        )}
        {state === "setPassword" && (
          <SetPassword
            clientId={config?.clientId}
            onPasswordSet={(password) => {
              // Password is now stored in localStorage by SetPassword component
              // No API call needed - just navigate to 2FA setup
              setPendingPassword(false);
              setState("twoFactorSetup");
            }}
            onBack={() => {
              // Clear pending registration when going back
              localStorage.removeItem(`pendingRegistration_${config?.clientId}`);
              setPendingPassword(false);
              setState("register");
            }}
          />
        )}
        {state === "twoFactorSetup" && (
          <TwoFactorSetup
            email={auth.email}
            clientId={auth.clientId}
            onSetupComplete={() => setState("login")}
            onBack={() =>
              setState(pendingPassword ? "setPassword" : "register")
            }
            onBackToPassword={() => setState("setPassword")}
            showSnackbar={showSnackbar}
          />
        )}

        {/* Portal tabs: show tabs only when in portal states */}
        {(state === "messages" ||
          state === "credits" ||
          state === "settings") && (
          <div>
            {state === "credits" && <CreditHistory {...commonProps} />}
            {state === "messages" && (
              <Messages
                {...commonProps}
                onLogout={handleLogout}
                fetchCreditBalance={fetchCreditBalance}
                showCredits={showCredit}
                setShowPurchaseModal={setShowPurchaseModal}
                onMessageSent={handleMessageSent}
              />
            )}
            {state === "settings" && (
              <Settings {...commonProps} onLogout={handleLogout} hideTabs />
            )}
          </div>
        )}
      </div>
      {showPurchaseModal && consumerDetails && (
        <CreditPurchase
          consumerDetails={consumerDetails}
          showPurchaseModal={showPurchaseModal}
          setShowPurchaseModal={setShowPurchaseModal}
          onCreditsPurchased={(creditsPurchased) => {
            animateCreditIncrease(
              showCredit,
              showCredit + creditsPurchased,
              setShowCredit,
              3000
            );
          }}
        />
      )}

      {/* Payment Result Popup */}
      {showPaymentResult && paymentResult && (
        <PaymentResultPopup
          show={showPaymentResult}
          result={{
            type: paymentResult.type === "success" ? "success" : 
                  paymentResult.type === "failure" ? "failed" : "abort",
            orderid: paymentResult.orderid || "",
            status: paymentResult.status || "Processing payment...",
            credits: paymentResult.credits
          }}
          onClose={() => {
            setShowPaymentResult(false);
            setPaymentResult(null);
          }}
        />
      )}

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={hideSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Widget;
